import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ASSISTANTS } from '../data/assistants'
import apiService from '../services/api'

const AuriIntakeModal = ({ isOpen, onClose, onProceed }) => {
  const [ideaDescription, setIdeaDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [auriResponse, setAuriResponse] = useState(null)
  const [error, setError] = useState('')
  const [showMobileNav, setShowMobileNav] = useState(false)

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!ideaDescription.trim()) {
      setError('Please describe your idea')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const response = await apiService.auriIntake({ idea_description: ideaDescription })
      setAuriResponse(response)
    } catch (err) {
      setError('Something went wrong. Please try again.')
      console.error('Auri intake error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleProceed = () => {
    onProceed(ideaDescription, auriResponse)
    setIdeaDescription('')
    setAuriResponse(null)
    setError('')
    setShowMobileNav(false)
  }

  const handleClose = () => {
    setIdeaDescription('')
    setAuriResponse(null)
    setError('')
    setShowMobileNav(false)
    onClose()
  }

  const basePath = import.meta.env.BASE_URL

  return (
    <>
      {/* Mobile Layout - Full screen */}
      <div className="fixed inset-0 z-50 flex flex-col bg-gray-900 sm:hidden">
        {/* Mobile Header Row 1 - Opptora branding + nav menu */}
        <div className="flex-shrink-0 bg-gray-800 border-b border-white/10 relative">
          <div className="flex h-12 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <img src={`${basePath}images/logo.png`} alt="Opptora" className="w-6 h-6" />
              <span className="text-white font-semibold text-base">Opptora</span>
            </div>
            {/* Nav menu button */}
            <button
              onClick={() => setShowMobileNav(!showMobileNav)}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {showMobileNav && (
            <div className="absolute top-full right-0 left-0 bg-gray-800 border-b border-white/10 shadow-lg z-50">
              <nav className="py-2">
                <a href="/app" className="block px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                  Dashboard
                </a>
                <a href="/app" className="block px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                  My Ideas
                </a>
                <button
                  onClick={handleClose}
                  className="w-full text-left px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  Close
                </button>
              </nav>
            </div>
          )}
        </div>

        {/* Mobile Header Row 2 - Context + close */}
        <div className="flex-shrink-0 bg-gray-800/50 border-b border-white/10">
          <div className="flex h-11 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                <img
                  src={`${basePath}characters/Auri/1_Default/1_wave.png`}
                  alt="Auri"
                  className="w-full h-full object-cover object-top scale-[1.8] translate-y-1.5"
                />
              </div>
              <span className="text-white text-sm font-medium">
                {auriResponse ? 'Your Team' : 'New Idea'}
              </span>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {!auriResponse ? (
            <>
              {/* Auri intro with avatar */}
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                  <img
                    src={`${basePath}characters/Auri/1_Default/1_wave.png`}
                    alt="Auri"
                    className="w-full h-full object-cover object-top scale-[1.8] translate-y-2"
                  />
                </div>
                <div className="flex-1 px-4 py-3 rounded-2xl bg-gray-800 text-gray-100">
                  <p className="text-sm">
                    Hi, I&apos;m Auri! I&apos;ll build the perfect team for your idea. Share your thoughts and I&apos;ll recommend specialist assistants to help.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="idea-description-mobile" className="block text-sm font-medium text-gray-300 mb-2">
                    What&apos;s your idea?
                  </label>
                  <textarea
                    id="idea-description-mobile"
                    value={ideaDescription}
                    onChange={(e) => setIdeaDescription(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 text-white text-base resize-none"
                    placeholder="Give me a high-level description of what you want to create..."
                    disabled={isLoading}
                  />
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg">
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}
              </form>
            </>
          ) : (
            <>
              {/* Auri response with avatar */}
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                  <img
                    src={`${basePath}characters/Auri/1_Default/1_wave.png`}
                    alt="Auri"
                    className="w-full h-full object-cover object-top scale-[1.8] translate-y-2"
                  />
                </div>
                <div className="flex-1 px-4 py-3 rounded-2xl bg-gray-800 text-gray-100">
                  <p className="text-sm whitespace-pre-wrap">{auriResponse.message}</p>
                </div>
              </div>

              {auriResponse.team_members && auriResponse.team_members.length > 0 && (
                <div className="mb-4 p-4 bg-gray-900/50 rounded-xl border border-gray-700">
                  <h3 className="text-sm font-medium text-gray-400 mb-3">Your Team</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {auriResponse.team_members.map((memberName) => {
                      const memberKey = memberName.toLowerCase()
                      const assistant = ASSISTANTS[memberKey]
                      if (!assistant) return null
                      return (
                        <div key={memberName} className="flex flex-col items-center gap-1 px-2 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-center">
                          <img
                            src={assistant.poses.default}
                            alt={assistant.name}
                            className="w-10 h-10 object-contain"
                          />
                          <div className="text-xs font-medium text-white">{assistant.name}</div>
                          <div className="text-[10px] text-gray-400 leading-tight">{assistant.title}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {auriResponse.next_steps && (
                <div className="mb-4 p-4 bg-emerald-900/20 rounded-xl border border-emerald-800">
                  <h3 className="text-sm font-medium text-emerald-400 mb-1">Next Steps</h3>
                  <p className="text-sm text-emerald-300">{auriResponse.next_steps}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Mobile Footer Actions */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 bg-gray-800">
          {!auriResponse ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-3 border border-gray-600 rounded-xl text-gray-300 hover:bg-gray-700 transition-colors text-base"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading || !ideaDescription.trim()}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Thinking...
                  </>
                ) : (
                  'Share with Auri'
                )}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setAuriResponse(null)
                  setIdeaDescription('')
                }}
                className="flex-1 px-4 py-3 border border-gray-600 rounded-xl text-gray-300 hover:bg-gray-700 transition-colors text-base"
              >
                Start Over
              </button>
              <button
                onClick={handleProceed}
                className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-colors flex items-center justify-center text-base"
              >
                Continue with Orin
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Layout - Modal with floating character */}
      <div className="hidden sm:block fixed inset-0 z-50 overflow-hidden">
        <div className="flex items-start justify-center min-h-screen px-4 pb-20 text-center" style={{ paddingTop: '200px' }}>
          <div className="fixed inset-0 bg-gray-900/80 transition-opacity" />

          <div className={`relative inline-block align-bottom bg-gray-800 rounded-2xl text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle w-full max-w-[900px] border border-white/10 min-h-[450px] ${auriResponse ? 'max-h-[calc(100vh-200px)]' : 'max-h-[calc(100vh-14rem-168px)]'}`}>
            <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-64 h-64 z-50">
              <img
                src={`${basePath}characters/Auri/1_Default/1_wave.png`}
                alt="Auri"
                className="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>

            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className={`px-6 pt-20 pb-6 overflow-y-auto min-h-[420px] ${auriResponse ? 'max-h-[calc(100vh-280px)]' : 'max-h-[calc(100vh-18rem-168px)]'}`}>
              {!auriResponse ? (
                <>
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'var(--font-handwritten)' }}>
                      Hi, I&apos;m Auri, your guide through this journey!
                    </h2>
                    <p className="text-gray-300 text-base">
                      I&apos;ll build the perfect team for your idea, and support you at every step of the way.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="idea-description" className="block text-sm font-medium text-gray-300 mb-2">
                        Please share high-level details of your idea, and I&apos;ll recommend the team of specialist assistants who can help
                      </label>
                      <textarea
                        id="idea-description"
                        value={ideaDescription}
                        onChange={(e) => setIdeaDescription(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 text-white resize-none"
                        placeholder="Give me a high-level description of what you want to create..."
                        disabled={isLoading}
                      />
                    </div>

                    {error && (
                      <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg">
                        <p className="text-sm text-red-300">{error}</p>
                      </div>
                    )}

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isLoading || !ideaDescription.trim()}
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Thinking...
                          </>
                        ) : (
                          'Share with Auri'
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <div className="prose prose-invert max-w-none">
                      <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {auriResponse.message}
                      </p>
                    </div>
                  </div>

                  {auriResponse.team_members && auriResponse.team_members.length > 0 && (
                    <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700">
                      <h3 className="text-sm font-medium text-gray-400 mb-3">Your Team</h3>
                      <div className="grid grid-cols-5 gap-2">
                        {auriResponse.team_members.map((memberName) => {
                          const memberKey = memberName.toLowerCase()
                          const assistant = ASSISTANTS[memberKey]
                          if (!assistant) return null
                          return (
                            <div key={memberName} className="flex flex-col items-center gap-1 px-2 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-center">
                              <img
                                src={assistant.poses.default}
                                alt={assistant.name}
                                className="w-10 h-10 object-contain"
                              />
                              <div className="text-xs font-medium text-white">{assistant.name}</div>
                              <div className="text-[10px] text-gray-400 leading-tight">{assistant.title}</div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {auriResponse.next_steps && (
                    <div className="mb-6 p-4 bg-emerald-900/20 rounded-xl border border-emerald-800">
                      <h3 className="text-sm font-medium text-emerald-400 mb-1">Next Steps</h3>
                      <p className="text-sm text-emerald-300">{auriResponse.next_steps}</p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setAuriResponse(null)
                        setIdeaDescription('')
                      }}
                      className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
                    >
                      Start Over
                    </button>
                    <button
                      onClick={handleProceed}
                      className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors flex items-center"
                    >
                      Continue with Orin
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

AuriIntakeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired
}

export default AuriIntakeModal
